import { useState } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { toast } from "sonner";
import { Plus, Copy, Trash2, Save, Dumbbell, Utensils, StickyNote, ChevronDown, ChevronUp } from "lucide-react";
import { plansAPI } from "../lib/api";

export default function WeeklyPlanEditor({ clientId, plan, onUpdate }) {
  const [weeks, setWeeks] = useState(plan?.weeks || []);
  const [activeWeek, setActiveWeek] = useState(1);
  const [saving, setSaving] = useState(false);
  const [expandedDay, setExpandedDay] = useState(null);

  const currentWeek = weeks.find(w => w.week_number === activeWeek) || { week_number: activeWeek, workout_days: [], diet_meals: [], notes: "" };
  const currentWeekIdx = weeks.findIndex(w => w.week_number === activeWeek);

  const updateCurrentWeek = (updated) => {
    const newWeeks = [...weeks];
    if (currentWeekIdx >= 0) {
      newWeeks[currentWeekIdx] = updated;
    } else {
      newWeeks.push(updated);
      newWeeks.sort((a, b) => a.week_number - b.week_number);
    }
    setWeeks(newWeeks);
  };

  const addWorkoutDay = () => {
    const dayNum = (currentWeek.workout_days?.length || 0) + 1;
    const updated = {
      ...currentWeek,
      workout_days: [...(currentWeek.workout_days || []), { day_name: `Day ${dayNum}`, exercises: [] }]
    };
    updateCurrentWeek(updated);
  };

  const addExercise = (dayIdx) => {
    const days = [...currentWeek.workout_days];
    days[dayIdx] = {
      ...days[dayIdx],
      exercises: [...(days[dayIdx].exercises || []), { exercise_name: "", sets: 3, reps: 10, weight: null, notes: "" }]
    };
    updateCurrentWeek({ ...currentWeek, workout_days: days });
  };

  const updateExercise = (dayIdx, exIdx, field, value) => {
    const days = [...currentWeek.workout_days];
    const exercises = [...days[dayIdx].exercises];
    exercises[exIdx] = { ...exercises[exIdx], [field]: value };
    days[dayIdx] = { ...days[dayIdx], exercises };
    updateCurrentWeek({ ...currentWeek, workout_days: days });
  };

  const removeExercise = (dayIdx, exIdx) => {
    const days = [...currentWeek.workout_days];
    days[dayIdx] = { ...days[dayIdx], exercises: days[dayIdx].exercises.filter((_, i) => i !== exIdx) };
    updateCurrentWeek({ ...currentWeek, workout_days: days });
  };

  const removeDay = (dayIdx) => {
    updateCurrentWeek({ ...currentWeek, workout_days: currentWeek.workout_days.filter((_, i) => i !== dayIdx) });
  };

  const updateDayName = (dayIdx, name) => {
    const days = [...currentWeek.workout_days];
    days[dayIdx] = { ...days[dayIdx], day_name: name };
    updateCurrentWeek({ ...currentWeek, workout_days: days });
  };

  const addMeal = () => {
    const updated = {
      ...currentWeek,
      diet_meals: [...(currentWeek.diet_meals || []), { meal_name: "", time: "", foods: "", notes: "" }]
    };
    updateCurrentWeek(updated);
  };

  const updateMeal = (mealIdx, field, value) => {
    const meals = [...currentWeek.diet_meals];
    meals[mealIdx] = { ...meals[mealIdx], [field]: value };
    updateCurrentWeek({ ...currentWeek, diet_meals: meals });
  };

  const removeMeal = (mealIdx) => {
    updateCurrentWeek({ ...currentWeek, diet_meals: currentWeek.diet_meals.filter((_, i) => i !== mealIdx) });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await plansAPI.update(clientId, plan.id, { weeks });
      onUpdate?.(res.data);
      toast.success("Plan saved");
    } catch {
      toast.error("Failed to save plan");
    } finally {
      setSaving(false);
    }
  };

  const handleDuplicate = async (sourceWeek) => {
    const nextWeek = sourceWeek + 1;
    if (nextWeek > plan.duration_weeks) {
      toast.error("No next week to duplicate to");
      return;
    }
    try {
      const res = await plansAPI.duplicateWeek(clientId, plan.id, sourceWeek, nextWeek);
      setWeeks(res.data.weeks);
      setActiveWeek(nextWeek);
      toast.success(`Week ${sourceWeek} duplicated to Week ${nextWeek}`);
      onUpdate?.(res.data);
    } catch {
      toast.error("Failed to duplicate week");
    }
  };

  return (
    <div className="space-y-4">
      {/* Week Tabs + Actions */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex gap-2 overflow-x-auto">
          {Array.from({ length: plan?.duration_weeks || 4 }, (_, i) => i + 1).map(wn => (
            <button
              key={wn}
              onClick={() => setActiveWeek(wn)}
              className={`px-4 py-2 rounded-xl text-xs font-bold uppercase tracking-wide whitespace-nowrap transition-smooth ${
                activeWeek === wn
                  ? 'bg-[#00BFFF]/20 text-[#00BFFF] border border-[#00BFFF]/30'
                  : 'bg-white/[0.02] text-muted-foreground border border-white/[0.04] hover:border-[#00BFFF]/15'
              }`}
              data-testid={`edit-week-${wn}`}
            >
              Week {wn}
            </button>
          ))}
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" className="btn-secondary text-xs" onClick={() => handleDuplicate(activeWeek)} data-testid="duplicate-week-btn">
            <Copy className="w-3 h-3 mr-1" /> Duplicate
          </Button>
          <Button size="sm" className="btn-premium text-white text-xs" onClick={handleSave} disabled={saving} data-testid="save-plan-btn">
            <Save className="w-3 h-3 mr-1" /> {saving ? "Saving..." : "Save"}
          </Button>
        </div>
      </div>

      {/* Workout Section */}
      <Card className="glass-card">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="font-heading text-base uppercase tracking-wide flex items-center gap-2">
              <Dumbbell className="w-4 h-4 text-[#00BFFF]" /> Workout — Week {activeWeek}
            </CardTitle>
            <Button size="sm" variant="ghost" onClick={addWorkoutDay} className="text-[#00BFFF] text-xs" data-testid="add-day-btn">
              <Plus className="w-3 h-3 mr-1" /> Add Day
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {currentWeek.workout_days?.map((day, di) => (
            <div key={di} className="progress-glass p-3 space-y-2" data-testid={`edit-day-${di}`}>
              <div className="flex items-center justify-between">
                <Input
                  value={day.day_name}
                  onChange={e => updateDayName(di, e.target.value)}
                  className="h-8 w-32 bg-transparent border-white/[0.06] text-sm font-bold"
                  data-testid={`day-name-${di}`}
                />
                <div className="flex items-center gap-1">
                  <button onClick={() => setExpandedDay(expandedDay === di ? null : di)} className="text-muted-foreground hover:text-foreground p-1">
                    {expandedDay === di ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </button>
                  <button onClick={() => removeDay(di)} className="text-red-400/60 hover:text-red-400 p-1" data-testid={`remove-day-${di}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
              {(expandedDay === di || expandedDay === null) && (
                <>
                  {day.exercises?.map((ex, ei) => (
                    <div key={ei} className="grid grid-cols-12 gap-2 items-center" data-testid={`exercise-${di}-${ei}`}>
                      <Input value={ex.exercise_name} onChange={e => updateExercise(di, ei, 'exercise_name', e.target.value)} placeholder="Exercise" className="col-span-4 h-8 bg-transparent border-white/[0.04] text-xs" />
                      <Input type="number" value={ex.sets} onChange={e => updateExercise(di, ei, 'sets', parseInt(e.target.value) || 0)} placeholder="Sets" className="col-span-2 h-8 bg-transparent border-white/[0.04] text-xs text-center" />
                      <Input type="number" value={ex.reps} onChange={e => updateExercise(di, ei, 'reps', parseInt(e.target.value) || 0)} placeholder="Reps" className="col-span-2 h-8 bg-transparent border-white/[0.04] text-xs text-center" />
                      <Input type="number" value={ex.weight || ''} onChange={e => updateExercise(di, ei, 'weight', parseFloat(e.target.value) || null)} placeholder="kg" className="col-span-2 h-8 bg-transparent border-white/[0.04] text-xs text-center" />
                      <button onClick={() => removeExercise(di, ei)} className="col-span-2 text-red-400/50 hover:text-red-400 flex justify-center">
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                  <Button size="sm" variant="ghost" onClick={() => addExercise(di)} className="text-[#00BFFF]/70 text-xs w-full" data-testid={`add-exercise-${di}`}>
                    <Plus className="w-3 h-3 mr-1" /> Exercise
                  </Button>
                </>
              )}
            </div>
          ))}
          {!currentWeek.workout_days?.length && (
            <p className="text-xs text-muted-foreground text-center py-4">No workout days. Click "Add Day" to start.</p>
          )}
        </CardContent>
      </Card>

      {/* Diet Section */}
      <Card className="glass-card">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="font-heading text-base uppercase tracking-wide flex items-center gap-2">
              <Utensils className="w-4 h-4 text-[#00BFFF]" /> Diet — Week {activeWeek}
            </CardTitle>
            <Button size="sm" variant="ghost" onClick={addMeal} className="text-[#00BFFF] text-xs" data-testid="add-meal-btn">
              <Plus className="w-3 h-3 mr-1" /> Add Meal
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {currentWeek.diet_meals?.map((meal, mi) => (
            <div key={mi} className="progress-glass p-3 space-y-2" data-testid={`edit-meal-${mi}`}>
              <div className="grid grid-cols-12 gap-2">
                <Input value={meal.meal_name} onChange={e => updateMeal(mi, 'meal_name', e.target.value)} placeholder="Meal name" className="col-span-5 h-8 bg-transparent border-white/[0.04] text-xs" />
                <Input value={meal.time} onChange={e => updateMeal(mi, 'time', e.target.value)} placeholder="Time" className="col-span-4 h-8 bg-transparent border-white/[0.04] text-xs" />
                <button onClick={() => removeMeal(mi)} className="col-span-3 text-red-400/50 hover:text-red-400 flex items-center justify-center">
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
              <Textarea value={meal.foods} onChange={e => updateMeal(mi, 'foods', e.target.value)} placeholder="Foods..." rows={2} className="bg-transparent border-white/[0.04] text-xs resize-none" />
            </div>
          ))}
          {!currentWeek.diet_meals?.length && (
            <p className="text-xs text-muted-foreground text-center py-4">No meals. Click "Add Meal" to start.</p>
          )}
        </CardContent>
      </Card>

      {/* Week Notes */}
      <Card className="glass-card">
        <CardHeader className="pb-2">
          <CardTitle className="font-heading text-base uppercase tracking-wide flex items-center gap-2">
            <StickyNote className="w-4 h-4 text-[#00BFFF]" /> Notes — Week {activeWeek}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Textarea
            value={currentWeek.notes || ""}
            onChange={e => updateCurrentWeek({ ...currentWeek, notes: e.target.value })}
            placeholder="Coach notes for this week..."
            rows={3}
            className="bg-transparent border-white/[0.04] text-sm resize-none"
            data-testid="week-notes"
          />
        </CardContent>
      </Card>
    </div>
  );
}
